import { useFastRefreshFixer } from 'src/hooks/dev/useFastRefreshFixer';
import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { useTranslate } from 'react-t7e';
import SwiperClass from 'swiper/types/swiper-class';
import {
  GenericSectionHeader,
  NavigationButton,
} from 'src/modules/materials/components/ShowcasePage/common';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Thumbnail } from 'src/modules/materials/components/Thumbnail';
import { Theme } from 'vendor/mods/styled-components';
import { darkTheme, dirRTL } from 'src/styles/theme';
import { ChevronLeft } from 'src/components/icons/ChevronLeft';
import { ChevronRight } from 'src/components/icons/ChevronRight';
import styled from 'styled-components';
import { Button } from 'src/components/common/Button';
import { ar } from 'src/styles/common';
import { TrackedLink } from 'src/components/TrackedLink';
import { route } from 'src/services/route';
import { Skeleton } from 'src/components/common/Skeleton';
import range from 'lodash/range';
import { useIsRTL } from 'src/hooks/useIsRTL';
import { ShowcaseListRenderComponentProps } from 'src/modules/materials/models/showcase';
import { TaxonomyContext } from 'src/modules/materials/providers/TaxonomyContext';
import { ShowcaseContext } from 'src/modules/materials/providers/ShowcaseContext';
import { motion, AnimatePresence } from 'framer-motion';
import { makeTransition } from 'src/utils/motion/transitions';
import { useDelayedValue } from 'src/hooks/useDelayedValue';

const StyledSwiper = styled(Swiper)`
  padding-left: var(--results-left-margin);
  padding-right: calc(var(--constraint-container-right-margin) - 20px);
  ${dirRTL} {
    padding-left: calc(var(--constraint-container-right-margin) - 20px);
    padding-right: var(--results-left-margin);
  }

  .swiper-slide {
    width: 264px;
    min-height: 220px;
    margin-right: 20px;
    ${dirRTL} {
      margin-right: 0;
      margin-left: 20px;
    }
  }
`;

const SkeletonSlide = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99;
  background: ${({ theme }) => theme.palette.background};
`;

const SwiperArea = styled.div`
  position: relative;
  overflow: hidden;
  margin: 0 calc(-1 * var(--results-right-margin)) 40px
    calc(-1 * var(--results-left-margin));
  ${NavigationButton} {
    opacity: 0;
  }
  &:hover > ${NavigationButton} {
    opacity: 1;
  }
  ${dirRTL} {
    margin: 0 calc(-1 * var(--results-left-margin)) 42px
      calc(-1 * var(--results-right-margin));
  }
`;

const SeeAllContainer = styled.div`
  position: relative;
  ${ar(16 / 9)}
`;

const SeeAllButton = styled(Button)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

export const SliderSectionRender: FC<ShowcaseListRenderComponentProps> = ({
  section,
  items,
  event,
  header = true,
  isLoading,
}) => {
  const key = useFastRefreshFixer();
  const prevRef = useRef<HTMLButtonElement>();
  const nextRef = useRef<HTMLButtonElement>();
  const [swiper, setSwiper] = useState<SwiperClass | null>(null);

  const { thumbnailOptions } = useContext(ShowcaseContext);

  const isRTL = useIsRTL();

  const { _ } = useTranslate();

  useEffect(() => {
    if (swiper && prevRef.current && nextRef.current && swiper.navigation) {
      swiper.navigation.init();
      swiper.navigation.update();
    }
  }, [swiper]);

  const hasItems = isLoading || (items != null && items.length > 0);
  const loading = useDelayedValue(isLoading, 500);

  const shouldDisplaySeeAllBtn = !!(
    section.searchQuery &&
    section.variables?.limit &&
    items &&
    items.length >= section.variables.limit
  );

  return (
    <TaxonomyContext.Provider value={section.taxonomy}>
      {header && <GenericSectionHeader section={section} />}

      <SwiperArea key={String(isRTL) + key}>
        {hasItems && (
          <>
            <StyledSwiper
              key={key}
              threshold={15}
              onSwiper={setSwiper}
              slidesPerView="auto"
              watchSlidesProgress
              navigation={{
                prevEl: prevRef.current,
                nextEl: nextRef.current,
              }}
              wrapperTag="ul"
            >
              {(items ?? range(5))?.map((item, i) => (
                <SwiperSlide key={i} tag="li">
                  <AnimatePresence>
                    {loading && (
                      <SkeletonSlide
                        key={`skeleton-${i}`}
                        {...makeTransition({
                          fade: true,
                        })}
                      >
                        <Skeleton />
                        <Skeleton
                          style={{ marginTop: 14 }}
                          width="60%"
                          height="20px"
                        />
                      </SkeletonSlide>
                    )}
                    {typeof item !== 'number' && (
                      <Thumbnail
                        item={item}
                        event={event}
                        uniqueId={item.id + section.name}
                        {...thumbnailOptions}
                      />
                    )}
                  </AnimatePresence>
                </SwiperSlide>
              ))}

              {section.searchQuery && shouldDisplaySeeAllBtn && (
                <SwiperSlide tag="li">
                  <SeeAllContainer>
                    <TrackedLink
                      href={route.search(section.searchQuery).all(true)}
                      passHref
                    >
                      <SeeAllButton
                        forwardedAs="a"
                        icon={ChevronRight}
                        iconProps={{ position: 'right', size: 12 }}
                      >
                        {_('See all')}
                      </SeeAllButton>
                    </TrackedLink>
                  </SeeAllContainer>
                </SwiperSlide>
              )}
            </StyledSwiper>

            <Theme theme={darkTheme}>
              <NavigationButton
                kind="circled"
                ref={prevRef}
                icon={ChevronLeft}
                $direction="left"
              />
              <NavigationButton
                kind="circled"
                ref={nextRef}
                icon={ChevronRight}
                $direction="right"
              />
            </Theme>
          </>
        )}
      </SwiperArea>
    </TaxonomyContext.Provider>
  );
};
